import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '../../lib/utils';

/**
 * Bis hierher hat jede Seite ihre Knoepfe selbst ausgeschrieben. Auf der
 * Mandantenverwaltung standen dadurch drei Groessen, zwei Radien und zwei
 * Schriftstaerken nebeneinander. Hier einmal benannt.
 *
 * `loading` blendet einen Spinner ein *und* sperrt den Knopf - beides war
 * vorher an jeder Stelle von Hand nachgebaut und mal vergessen.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium whitespace-nowrap transition ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ' +
    'disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      tone: {
        primary: 'bg-akzent text-auf-akzent shadow-sm hover:bg-akzent-tief',
        secondary: 'border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50',
        ghost: 'text-slate-500 hover:bg-slate-100 hover:text-slate-700',
        danger: 'border border-red-200 bg-white text-red-600 hover:border-red-300 hover:bg-red-50',
        // Fuer den abschliessenden Schritt eines Vorgangs ("Erledigt melden").
        // Bewusst eigener Ton statt `primary`: Auf derselben Karte steht meist
        // noch eine blaue Handlung, und beide duerfen nicht gleich aussehen.
        success: 'bg-erfolg text-auf-akzent shadow-sm hover:bg-erfolg-tief',
      },
      size: {
        sm: 'px-3 py-1.5 text-xs',
        md: 'px-4 py-2 text-sm',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: { tone: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  className, tone, size, asChild = false, loading = false, disabled, children, ...props
}) => {
  /**
   * `asChild` gibt die Gestaltung an ein fremdes Element weiter - meist einen
   * Link. `Slot` verlangt dafuer *genau ein* Kindelement.
   *
   * Eine erste Fassung reichte hier zusaetzlich den Spinner-Platz durch, also
   * zwei Kinder. Das riss jede Seite mit `<Button asChild>` mitsamt Umgebung
   * herunter ("React.Children.only expected to receive a single React element
   * child") - aufgefallen an der 404-Seite. Auch `disabled` faellt hier weg:
   * Ein `<a disabled>` gibt es nicht.
   */
  if (asChild) {
    return (
      <Slot className={cn(buttonVariants({ tone, size }), className)} {...props}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      className={cn(buttonVariants({ tone, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : null}
      {children}
    </button>
  );
};

export default Button;
